import _ from 'lodash'
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { usePatientIdentifierLabel } from '@/hooks/usePatientIdentifierLabel'
import { Controller, FormProvider, SubmitErrorHandler, SubmitHandler, useForm, useFormContext, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import SignatureCanvas from 'react-signature-canvas'
import { createColumnHelper, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table'
import { t as linguiT } from '@lingui/macro'

import Accordion from '@mui/material/Accordion'
import AccordionDetails from '@mui/material/AccordionDetails'
import AccordionSummary from '@mui/material/AccordionSummary'
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import Divider from '@mui/material/Divider'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import FormLabel from '@mui/material/FormLabel'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import Switch from '@mui/material/Switch'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import Link from '@mui/material/Link'
import LoadingButton from '@mui/lab/LoadingButton'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TableCell from '@mui/material/TableCell'
import TableBody from '@mui/material/TableBody'
import Typography from '@mui/material/Typography'

import { styled } from '@mui/material/styles'
import CloseIcon from '@mui/icons-material/Close'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'

import colors from '@/assets/scss/_themes-vars.module.scss'

import PhoneNumberInputController from '@/components/ui/inputs/PhoneNumberInputController'
import RadioGroupInput from '@/components/ui/inputs/RadioGroupInput'
import Signature from '@/pages/configuration/components/Signature'
import TextInputController from '@/components/ui/inputs/TextInputController'
import AudioViewer from '@/components/ui/preview/AudioViewer'
import CheckboxInputController from '@/components/ui/inputs/CheckboxInputController'
import FilePreviewDialog from '@/components/ui/dialogs/FilePreviewDialog'
import { Checkbox, Paper } from '@mui/material'
import WaveSurfer from 'wavesurfer.js'

import { FormFieldType } from '@/core/models/form.model'
import { useIsUS } from '@/hooks/useIsUS'

// Function to convert labels to translation keys
function labelToTranslationKey(label: string) {
  return 'page.liveConsultation.mesures.values.' + _.deburr(label.replace(/[\s-]+/g, '')).toLowerCase()
}

// Schema for selecting files/documents
const docSchema = z.object({
  checked: z.boolean(),
  filename: z.string().min(1),
  key: z.string().min(1),
  url: z.string().min(1),
})

type Doc = z.infer<typeof docSchema>

const formSchema = z
  .object({
    establishment: z.object({
      address: z.string().optional(),
      name: z.string().min(1),
      phone: z.string().optional(),
    }),
    logo: z.string().min(1),
    title: z.string().min(1),
    startDate: z.string().min(1),
    pds: z.string().optional(),
    patient: z.object({
      firstName: z.string().optional(),
      lastName: z.string().optional(),
      gender: z.string().optional(),
      id: z.string().optional(),
      birthday: z.string().optional(),
      secu: z.string().optional(),
      attendingPhysician: z.string().optional(),
      insured: z.string().optional(),
      insuredRelationship: z.string().optional(),
      insuranceProvider: z.string().optional(),
      primaryInsurance: z.string().optional(),
      secondaryInsurance: z.string().optional(),
      primaryPolicy: z.string().optional(),
    }),
    mesures: z.array(
      z.object({
        label: z.string().min(1),
        unit: z.string().min(1),
        value: z.string().optional(),
        order: z.number(),
      })
    ),
    mesuresNote: z.string().optional(),
    biologicals: z.array(
      z.object({
        label: z.string().min(1),
        comment: z.string().optional(),
        unit: z.string().min(1),
        value: z.string().min(1),
        files: z.array(docSchema),
      })
    ),
    audios: z.array(docSchema),
    pdfs: z.array(docSchema),
    documents: z.array(docSchema),
    images: z.array(docSchema),
    stethoscopes: z.array(docSchema.extend({ waveform: z.string().optional() })),
    eSignature: z.string().optional(),
    manualSignature: z.string().optional(),
    signatureMode: z.enum(['manual', 'e-signature']),
    prescription: z.string().optional(),
    conclusion: z.string().optional(),
    selectedFormSubmissionId: z.string().optional(),
    formSubmissions: z
      .array(
        z.object({
          fieldId: z.string().min(1),
          label: z.string().min(1),
          type: z.string().min(1),
          required: z.boolean(),
          options: z.array(z.string().min(1)).optional(),
          value: z.any().nullable().optional(),
        })
      )
      .optional(),
    showConsultationLink: z.boolean().optional(),
    identityFieldsDisplay: z.object({
      usageSurname: z.boolean(),
      usageFirstName: z.boolean(),
      birthPlace: z.boolean(),
      birthPlaceCode: z.boolean(),
    }),
  })
  .superRefine((data, ctx) => {
    if (data.signatureMode === 'e-signature' && !data.eSignature) {
      ctx.addIssue({ code: 'custom', message: 'Required', path: ['eSignature'] })
    }
    if (data.signatureMode === 'manual' && !data.manualSignature) {
      ctx.addIssue({ code: 'custom', message: 'Required', path: ['manualSignature'] })
    }
  })

export type GenerateReportFormValues = z.infer<typeof formSchema>

export type GenerateReportDialogProps = {
  initialValues: GenerateReportFormValues
  conclusionSentences: string[]
  dictaphoneSentences: string[]
  isDoctor?: boolean
  isGenerating?: boolean
  onClose?: () => void
  onGenerateReport?: (reportValues: GenerateReportFormValues) => void
  open: boolean
  formSubmissionOptions?: Array<{
    id: string
    submittedAt?: string
    title?: string
    fields: Array<{ fieldId: string; label: string; type: string; required: boolean; options?: string[]; value: any }>
  }>
}

const ImageCheckbox = styled('div')({
  position: 'relative',
  paddingLeft: 0,
  boxShadow: '0px 2px 1px -1px rgb(0 0 0 / 20%), 0px 1px 1px 0px rgb(0 0 0 / 14%), 0px 1px 3px 0px rgb(0 0 0 / 12%)',
  borderRadius: '4px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '150px',
  '& input': {
    position: 'absolute',
    opacity: 0,
    top: 0,
    left: 0,
    '&:checked': {
      opacity: 1,
    },
  },
  '& label': {
    cursor: 'pointer',
    '& img': {
      borderRadius: '2.5px',
      maxWidth: '100%',
      height: 'auto',
    },
  },
})

export function GenerateReportDialog(props: GenerateReportDialogProps) {
  const {
    initialValues,
    conclusionSentences,
    dictaphoneSentences,
    isDoctor = false,
    isGenerating = false,
    onClose,
    onGenerateReport,
    open,
    formSubmissionOptions = [],
  } = props

  const { t } = useTranslation()

  const anchorRef = useRef<HTMLDivElement>(null)

  const rhForm = useForm<GenerateReportFormValues>({ defaultValues: initialValues, resolver: zodResolver(formSchema) })

  useEffect(() => {
    rhForm.reset(initialValues)
  }, [initialValues])

  const onSubmit: SubmitHandler<GenerateReportFormValues> = (values) => {
    onGenerateReport?.(values)
  }

  const onInvalid: SubmitErrorHandler<GenerateReportFormValues> = (errors) => {
    anchorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  return (
    <Dialog onClose={onClose} open={open} PaperProps={{ sx: { maxWidth: '800px' } }}>
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography>{t('page.liveConsultation.generateReportDialog.title')}</Typography>
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              color: (theme) => theme.palette.grey[500],
              transform: 'translateX(25%)',
            }}
          >
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <FormProvider {...rhForm}>
          <form>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={3} container alignItems="center" justifyContent="center">
                <UploadLogoButton />
              </Grid>
              <Grid item xs={12} sm={9}>
                <div ref={anchorRef}></div>
                <EstablishmentSection />
              </Grid>

              <Grid item xs={12}>
                <CheckboxInputController control={rhForm.control} label={linguiT`Show consultation link`} name="showConsultationLink" />
              </Grid>
              <Grid item xs={12}>
                <PatientInfoSection />
                <IdentityFieldsSection />
                <FormSubmissionsSection formSubmissionOptions={formSubmissionOptions} />
                <MesuresSection />
                <BiologicalsSection />
                <ImagesSection />
                <WaveFormsSection />
                <PdfsSection />
                <PrescriptionSection conclusionSentences={conclusionSentences} dictaphoneSentences={dictaphoneSentences} />
                <ConclusionSection conclusionSentences={conclusionSentences} dictaphoneSentences={dictaphoneSentences} />
                <SignatureSection initialValues={initialValues} />
              </Grid>
            </Grid>
          </form>
        </FormProvider>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center' }}>
        <LoadingButton loading={isGenerating} onClick={rhForm.handleSubmit(onSubmit, onInvalid)} variant="contained">
          {t('page.liveConsultation.generateReportDialog.generateReportButton.label')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}

function SentencesDialog({
  field,
  onClose,
  open,
  sentences,
}: {
  field: 'conclusion' | 'prescription'
  onClose: () => void
  open: boolean
  sentences: string[]
}) {
  const { t } = useTranslation()
  const rhForm = useFormContext<GenerateReportFormValues>()
  return (
    <Dialog onClose={onClose} open={open} fullWidth maxWidth="md" PaperProps={{ sx: { minHeight: '70%', pt: 5, px: 5 } }} scroll="paper">
      <DialogTitle>
        <Typography variant="h2">{t('page.liveConsultation.generateReportDialog.sections.conclusionSentencesDialog.title')}</Typography>
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Stack direction="column" sx={{ mt: '20px' }} spacing="20px">
          {sentences.map((sentence: string) => (
            <Card
              sx={{ ':hover': { boxShadow: 2 }, p: 2, cursor: 'pointer' }}
              onClick={() => {
                rhForm.setValue(field, sentence)
                onClose()
              }}
            >
              <Typography>{sentence}</Typography>
            </Card>
          ))}
        </Stack>
      </DialogContent>
    </Dialog>
  )
}

function ConclusionSentencesButton({ conclusionSentences, field }: { conclusionSentences: string[]; field: 'conclusion' | 'prescription' }) {
  const { t } = useTranslation()
  const [openSentencesDialog, setOpenSentencesDialog] = useState(false)
  return (
    <>
      <Button
        style={{ marginRight: '5px' }}
        disabled={!conclusionSentences.length}
        color="primary"
        variant="outlined"
        onClick={() => setOpenSentencesDialog(true)}
      >
        {t('page.liveConsultation.generateReportDialog.sections.conclusion.conclusionSentencesButton.label')}
      </Button>
      <SentencesDialog field={field} onClose={() => setOpenSentencesDialog(false)} open={openSentencesDialog} sentences={conclusionSentences} />
    </>
  )
}

function DictaphoneSentencesButton({ dictaphoneSentences, field }: { dictaphoneSentences: string[]; field: 'conclusion' | 'prescription' }) {
  const { t } = useTranslation()
  const [openSentencesDialog, setOpenSentencesDialog] = useState(false)
  return (
    <>
      <Button disabled={!dictaphoneSentences.length} color="primary" variant="outlined" onClick={() => setOpenSentencesDialog(false)}>
        {t('page.liveConsultation.generateReportDialog.sections.conclusion.dictaphoneSentencesButton.label')}
      </Button>
      <SentencesDialog field={field} onClose={() => setOpenSentencesDialog(false)} open={openSentencesDialog} sentences={dictaphoneSentences} />
    </>
  )
}

function UploadLogoButton() {
  const logoInputRef = useRef<HTMLInputElement>(null)
  const rhForm = useFormContext<GenerateReportFormValues>()
  const logo = useWatch({ control: rhForm.control, name: 'logo' })
  return (
    <Button
      onClick={() => logoInputRef.current?.click()}
      sx={{
        width: '150px',
        height: '150px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        padding: 0,
        borderRadius: '100%',
      }}
    >
      <Avatar imgProps={{ sx: { objectFit: 'contain' } }} src={logo} alt="logo" sx={{ width: '100%', height: '100%' }} />
      <input
        ref={logoInputRef}
        name="logo"
        accept="image/*"
        type="file"
        hidden
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
          const files = event.target.files
          if (files) {
            const fileReader = new FileReader()
            fileReader.onload = () => {
              if (fileReader.readyState === fileReader.DONE && typeof fileReader.result === 'string') {
                rhForm.setValue('logo', fileReader.result)
              }
            }
            fileReader.readAsDataURL(files[0])
          }
        }}
      />
    </Button>
  )
}

function EstablishmentSection() {
  const { t } = useTranslation()
  const rhForm = useFormContext<GenerateReportFormValues>()
  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <TextInputController control={rhForm.control} fullWidth label={t('field.name.label')} name="establishment.name" />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextInputController control={rhForm.control} fullWidth label={t('field.address.label')} name="establishment.address" />
      </Grid>
      <Grid item xs={12} sm={6}>
        <PhoneNumberInputController control={rhForm.control} label={t('field.phone.label')} name="establishment.phone" />
      </Grid>
    </Grid>
  )
}

function DocumentLink({ file, truncateLength }: { file: Doc; truncateLength?: number }) {
  const [openDialog, setOpenDialog] = useState(false)
  return (
    <>
      <Link
        href={file.url}
        onClick={(event) => {
          event.preventDefault()
          setOpenDialog(true)
        }}
        underline="none"
      >
        {truncateLength ? _.truncate(file.filename, { length: truncateLength }) : file.filename}
      </Link>
      <FilePreviewDialog
        disableChangeFileName
        fileName={file.filename}
        fileType={file.filename.split('.')[1]}
        src={file.url}
        PhotoViewerProps={{ disableAnnotation: true }}
        open={openDialog}
        onClose={() => setOpenDialog(false)}
      />
    </>
  )
}

function BiologicalsSection() {
  const { t } = useTranslation()
  const rhForm = useFormContext<GenerateReportFormValues>()
  const biologicals = useWatch({ control: rhForm.control, name: 'biologicals' })

  const columnHelper = createColumnHelper<GenerateReportFormValues['biologicals'][number]>()

  const columns = useMemo(
    () => [
      columnHelper.accessor('label', { header: t('table.biologicals.header.label').toString() }),
      columnHelper.accessor('value', { header: t('table.biologicals.header.value').toString() }),
      columnHelper.accessor('unit', { header: t('table.biologicals.header.unit').toString() }),
      columnHelper.accessor('comment', {
        header: t('table.biologicals.header.comment').toString(),
        cell: ({ getValue }) => getValue() || t('component.group.biologicals.noComment'),
      }),
      columnHelper.accessor('files', {
        header: t('table.biologicals.header.documents').toString(),
        cell: ({ getValue, row }) => (
          <Stack>
            {getValue().map((file, fileIndex) => (
              <Stack key={file.key} direction="row" alignItems="center" justifyContent="space-between">
                <DocumentLink file={file} truncateLength={24} />
                <CheckboxInputController control={rhForm.control} name={`biologicals.${row.index}.files.${fileIndex}.checked`} />
              </Stack>
            ))}
          </Stack>
        ),
      }),
    ],
    [columnHelper, rhForm, t]
  )

  const table = useReactTable({
    columns,
    data: biologicals,
    getCoreRowModel: getCoreRowModel(),
  })

  return (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography>{t('page.liveConsultation.generateReportDialog.sections.biologicals.title')}</Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0, px: 0 }}>
        <Table>
          <TableHead>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableCell key={header.id} align={header.column.columnDef.meta?.align} scope="col">
                      {flexRender(header.column.columnDef.header, header.getContext())}
                    </TableCell>
                  )
                })}
              </TableRow>
            ))}
          </TableHead>
          <TableBody sx={{ position: 'relative' }}>
            {table.getRowModel().rows.map((row) => {
              return (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map((cell) => {
                    return (
                      <TableCell key={cell.id} align={cell.column.columnDef.meta?.align} className={cell.column.columnDef.meta?.className}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    )
                  })}
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </AccordionDetails>
    </Accordion>
  )
}

function ConclusionSection({ conclusionSentences, dictaphoneSentences }: { conclusionSentences: string[]; dictaphoneSentences: string[] }) {
  const { t } = useTranslation()
  const rhForm = useFormContext<GenerateReportFormValues>()
  return (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography>{t('page.liveConsultation.generateReportDialog.sections.conclusion.title')}</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Box mb={2}>
          <TextInputController control={rhForm.control} fullWidth maxRows={3} minRows={2} multiline name="conclusion" />
        </Box>
        <ConclusionSentencesButton conclusionSentences={conclusionSentences} field="conclusion" />
        <DictaphoneSentencesButton dictaphoneSentences={dictaphoneSentences} field="conclusion" />
      </AccordionDetails>
    </Accordion>
  )
}

function ImagesSection() {
  const { t } = useTranslation()
  const rhForm = useFormContext<GenerateReportFormValues>()
  const images = useWatch({ control: rhForm.control, name: 'images' })
  return (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography>{t('page.liveConsultation.generateReportDialog.sections.images.title')}</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={2}>
          {_.map(images, (image, index) => (
            <Grid key={index} item xs={6} sm={3}>
              <ImageCheckbox>
                <input {...rhForm.register(`images.${index}.checked`)} type="checkbox" id={image.filename} />
                <label htmlFor={image.filename}>
                  <Box component="img" src={image.url} alt="diagnostic-image" />
                </label>
              </ImageCheckbox>
            </Grid>
          ))}
        </Grid>
      </AccordionDetails>
    </Accordion>
  )
}

function PatientInfoSection() {
  const isUS = useIsUS()
  const { t } = useTranslation()
  const identifierLabel = usePatientIdentifierLabel()
  const rhForm = useFormContext<GenerateReportFormValues>()
  return (
    <Accordion expanded>
      <AccordionSummary>
        <Typography>{t('page.liveConsultation.generateReportDialog.sections.patientInfo.title')}</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={1}>
          <Grid item xs={6} sm={3}>
            <TextInputController control={rhForm.control} readOnly fullWidth label={t('field.firstName.label')} name="patient.firstName" />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextInputController control={rhForm.control} readOnly fullWidth label={t('field.lastName.label')} name="patient.lastName" />
          </Grid>
          <Grid item xs={5} sm={3}>
            <TextInputController control={rhForm.control} readOnly fullWidth label={t('field.gender.label')} name="patient.gender" />
          </Grid>
          <Grid item xs={7} sm={3}>
            <TextInputController control={rhForm.control} readOnly fullWidth label={t('field.birthday.label')} name="patient.birthday" />
          </Grid>
          {isUS && (
            <Grid item xs={12} sm={3}>
              <TextInputController control={rhForm.control} readOnly fullWidth label={t('field.insured.label')} name="patient.insured" />
            </Grid>
          )}
          {!isUS && (
            <Grid item xs={12} sm={3}>
              <TextInputController control={rhForm.control} readOnly fullWidth label={t('field.id.label')} name="patient.id" />
            </Grid>
          )}
          <Grid item xs={12} sm={3}>
            <TextInputController
              control={rhForm.control}
              readOnly
              fullWidth
              label={identifierLabel}
              name={isUS ? 'patient.primaryPolicy' : 'patient.secu'}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextInputController
              control={rhForm.control}
              readOnly
              fullWidth
              label={t('field.doctor.label', { context: 'attending' })}
              name="patient.attendingPhysician"
            />
          </Grid>
          {isUS && (
            <>
              <Grid item xs={12} sm={3}>
                <TextInputController control={rhForm.control} readOnly fullWidth label={t('field.insuranceProvider.label')} name="patient.insuranceProvider" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextInputController
                  control={rhForm.control}
                  readOnly
                  fullWidth
                  label={t('field.insuredRelationship.label')}
                  name="patient.insuredRelationship"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextInputController control={rhForm.control} readOnly fullWidth label={t('field.primaryInsurance.label')} name="patient.primaryInsurance" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextInputController
                  control={rhForm.control}
                  readOnly
                  fullWidth
                  label={t('field.secondaryInsurance.label')}
                  name="patient.secondaryInsurance"
                />
              </Grid>
            </>
          )}
        </Grid>
      </AccordionDetails>
    </Accordion>
  )
}

function IdentityFieldsSection() {
  const { t } = useTranslation()
  const rhForm = useFormContext<GenerateReportFormValues>()
  return (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography>{t('page.liveConsultation.generateReportDialog.sections.identityFields.title')}</Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0 }}>
        <Stack>
          <CheckboxInputController
            control={rhForm.control}
            name="identityFieldsDisplay.usageSurname"
            label={linguiT`Used name`}
          />
          <CheckboxInputController
            control={rhForm.control}
            name="identityFieldsDisplay.usageFirstName"
            label={linguiT`Used first name`}
          />
          <CheckboxInputController
            control={rhForm.control}
            name="identityFieldsDisplay.birthPlace"
            label={linguiT`Place of birth`}
          />
          <CheckboxInputController
            control={rhForm.control}
            name="identityFieldsDisplay.birthPlaceCode"
            label={linguiT`Birth place code`}
          />
        </Stack>
      </AccordionDetails>
    </Accordion>
  )
}

function PdfsSection() {
  const { t } = useTranslation()
  const rhForm = useFormContext<GenerateReportFormValues>()
  const pdfs = useWatch({ control: rhForm.control, name: 'pdfs' })

  const columnHelper = createColumnHelper<GenerateReportFormValues['pdfs'][number]>()

  const columns = useMemo(
    () => [
      columnHelper.accessor('filename', {
        header: t('page.liveConsultation.generateReportDialog.sections.documents.table.header.filename').toString(),
        cell: ({ row }) => (
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <DocumentLink file={row.original} />
            <CheckboxInputController control={rhForm.control} name={`pdfs.${row.index}.checked`} />
          </Stack>
        ),
      }),
    ],
    [columnHelper, t]
  )

  const table = useReactTable({
    columns,
    data: pdfs,
    getCoreRowModel: getCoreRowModel(),
  })

  return (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography>{t('page.liveConsultation.generateReportDialog.sections.pdfs.title')}</Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0, px: 0 }}>
        <Table sx={{ minWidth: 300 }}>
          <TableHead sx={{ bgcolor: colors.gray100 }}>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableCell key={header.id} align={header.column.columnDef.meta?.align} scope="col">
                      {flexRender(header.column.columnDef.header, header.getContext())}
                    </TableCell>
                  )
                })}
              </TableRow>
            ))}
          </TableHead>
          <TableBody sx={{ position: 'relative' }}>
            {table.getRowModel().rows.map((row, index) => {
              return (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id} align={cell.column.columnDef.meta?.align} className={cell.column.columnDef.meta?.className}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </AccordionDetails>
    </Accordion>
  )
}

function PrescriptionSection({ conclusionSentences, dictaphoneSentences }: { conclusionSentences: string[]; dictaphoneSentences: string[] }) {
  const { t } = useTranslation()
  const rhForm = useFormContext<GenerateReportFormValues>()
  return (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography>{t('page.liveConsultation.generateReportDialog.sections.prescription.title')}</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Box mb={2}>
          <TextInputController control={rhForm.control} fullWidth maxRows={3} minRows={2} multiline name="prescription" />
        </Box>
        <ConclusionSentencesButton conclusionSentences={conclusionSentences} field="prescription" />
        <DictaphoneSentencesButton dictaphoneSentences={dictaphoneSentences} field="prescription" />
      </AccordionDetails>
    </Accordion>
  )
}

function MesuresSection() {
  const { t } = useTranslation()
  const rhForm = useFormContext<GenerateReportFormValues>()
  const mesures = useWatch({ control: rhForm.control, name: 'mesures' })
  return (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography>{t('page.liveConsultation.generateReportDialog.sections.mesures.title')}</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={2}>
          {_.map(mesures, (mesure, index) => (
            <Grid key={index} item xs={6} sm={4}>
              <TextInputController
                control={rhForm.control}
                readOnly
                fullWidth
                label={`${t(labelToTranslationKey(mesure.label))} (${mesure.unit})`}
                name={`mesures.${index}.value`}
              />
            </Grid>
          ))}
          <Grid item xs={12}>
            <TextInputController control={rhForm.control} multiline fullWidth label={t('field.comment.label')} name="mesuresNote" />
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>
  )
}

function SignatureSection({ initialValues }: { initialValues: GenerateReportFormValues }) {
  const { t } = useTranslation()
  const rhForm = useFormContext<GenerateReportFormValues>()

  const sigCanvasRef = useRef<SignatureCanvas | null>(null)

  const [editSignatureMode, setEditSignatureMode] = useState<boolean>(true)
  const [selectedSignatureMode, setSelectedSignatureMode] = useState<string>(initialValues.signatureMode)

  const signatureModeOptions = useMemo(
    () => [
      { label: t('page.liveConsultation.generateReportDialog.sections.signature.mode.manual'), value: 'manual' },
      { label: t('page.liveConsultation.generateReportDialog.sections.signature.mode.eSignature'), value: 'e-signature' },
    ],
    [t]
  )

  const handleChangeSignatureMode = (_event: React.ChangeEvent<HTMLInputElement>, value: string) => {
    setSelectedSignatureMode(value)
    rhForm.setValue('signatureMode', value as 'manual' | 'e-signature')
    switch (value) {
      case 'e-signature':
        if (initialValues.eSignature) {
          sigCanvasRef.current?.fromDataURL(initialValues.eSignature)
          rhForm.setValue('eSignature', initialValues.eSignature)
        }
        break
      case 'manual':
        rhForm.setValue('manualSignature', initialValues.manualSignature)
        break
      default:
        break
    }
  }

  return (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography>{t('page.liveConsultation.generateReportDialog.sections.signature.title')}</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={1}>
          <RadioGroupInput onChange={handleChangeSignatureMode} options={signatureModeOptions} row value={selectedSignatureMode} />
          {selectedSignatureMode === 'manual' && <TextInputController control={rhForm.control} name="manualSignature" />}
          {selectedSignatureMode === 'e-signature' && (
            <Stack direction="row" spacing={2}>
              <Controller
                control={rhForm.control}
                name="eSignature"
                render={({ field: { value } }) => (
                  <Signature readOnly={!editSignatureMode} src={value ?? ''} onEnd={(dataUrl: string) => rhForm.setValue('eSignature', dataUrl)} />
                )}
              />
              <Box>
                <Button color="primary" onClick={() => setEditSignatureMode(!editSignatureMode)} variant="outlined">
                  {editSignatureMode ? t('common:button.edit.label') : t('common:button.save.label')}
                </Button>
              </Box>
            </Stack>
          )}
        </Stack>
      </AccordionDetails>
    </Accordion>
  )
}

function Waveform({ name, src }: { name: `stethoscopes.${number}.waveform`; src: string }) {
  const rhForm = useFormContext<GenerateReportFormValues>()

  const wavesurferRef = useRef<WaveSurfer>()
  const wsContainerRef = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    if (!wsContainerRef.current) return

    if (wavesurferRef.current) wavesurferRef.current.destroy()

    const wavesurfer = WaveSurfer.create({
      container: wsContainerRef.current,
      cursorWidth: 1,
      cursorColor: 'transparent',
      normalize: true,
      waveColor: 'blue',
      progressColor: 'grey',
      mediaControls: false,
      height: 160,
      interact: false,
    })

    wavesurfer
      .load(src)
      .then(() => wavesurfer.exportImage('image/png', 1, 'dataURL'))
      .then((waveformImageDataUri) => {
        rhForm.setValue(name, waveformImageDataUri[0])
      })

    wavesurfer.on('error', (error: any) => {
      console.log(error)
    })
    wavesurferRef.current = wavesurfer
  }, [name, src])

  return (
    <Paper sx={{ width: '100%' }}>
      <div ref={wsContainerRef}></div>
    </Paper>
  )
}

function WaveFormsSection() {
  const { t } = useTranslation()
  const rhForm = useFormContext<GenerateReportFormValues>()
  const stethoscopes = useWatch({ control: rhForm.control, name: 'stethoscopes' })
  return (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography>{t('page.liveConsultation.generateReportDialog.sections.audios.title')}</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Stack direction="column" spacing={2}>
          {stethoscopes.map((stethoscope, index) => (
            <Stack direction="row" alignItems="center" justifyContent="center">
              <CheckboxInputController control={rhForm.control} name={`stethoscopes.${index}.checked`} />
              <Waveform name={`stethoscopes.${index}.waveform`} src={stethoscope.url} />
            </Stack>
          ))}
        </Stack>
      </AccordionDetails>
    </Accordion>
  )
}

function FormSubmissionsSection({ formSubmissionOptions = [] as GenerateReportDialogProps['formSubmissionOptions'] }) {
  const { t } = useTranslation()
  const rhForm = useFormContext<GenerateReportFormValues>()
  const responses = useWatch({ control: rhForm.control, name: 'formSubmissions' })
  const selectedSubmissionId = useWatch({ control: rhForm.control, name: 'selectedFormSubmissionId' })
  return (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography>{t('page.liveConsultation.generateReportDialog.sections.forms.title')}</Typography>
      </AccordionSummary>
      <AccordionDetails>
        {!!formSubmissionOptions?.length && (
          <FormControl fullWidth sx={{ mb: 2 }}>
            <FormLabel>{t('page.liveConsultation.generateReportDialog.sections.forms.submission.selectLabel')}</FormLabel>
            <Select
              value={selectedSubmissionId || ''}
              displayEmpty
              onChange={(e) => {
                const option = formSubmissionOptions.find((o) => o?.id === e.target.value)
                if (!option) return
                rhForm.setValue('selectedFormSubmissionId', option.id, { shouldDirty: true })
                rhForm.setValue(
                  'formSubmissions',
                  option.fields.map((f) => ({ fieldId: f.fieldId, label: f.label, type: f.type, required: f.required, options: f.options, value: f.value })),
                  { shouldDirty: true }
                )
              }}
            >
              {formSubmissionOptions.map((opt) => {
                return (
                  <MenuItem key={opt.id} value={opt.id}>
                    {opt.title}
                  </MenuItem>
                )
              })}
            </Select>
          </FormControl>
        )}
        <Stack spacing={2}>
          {responses?.map((response, index) => {
            const fieldName = `formSubmissions.${index}.value` as const
            switch (response.type) {
              case FormFieldType.Boolean:
                return (
                  <Controller
                    key={response.fieldId}
                    control={rhForm.control}
                    name={fieldName}
                    render={({ field }) => {
                      const isChecked = field.value === true || field.value === 'Oui'
                      return (
                        <FormControlLabel
                          control={
                            <Checkbox
                              style={{ color: 'blue' }}
                              checked={isChecked}
                              onChange={(event) => {
                                const newValue = event.target.checked ? 'Oui' : 'Non'
                                field.onChange(newValue)
                              }}
                            />
                          }
                          label={<Typography style={{ fontSize: 14 }}>{response.label}</Typography>}
                          labelPlacement="end"
                        />
                      )
                    }}
                  />
                )
              case FormFieldType.Multiple:
                return (
                  <Controller
                    key={response.fieldId}
                    control={rhForm.control}
                    name={fieldName}
                    render={({ field }) => (
                      <FormControl fullWidth>
                        <FormLabel>{response.label}</FormLabel>
                        <Select
                          multiple
                          value={(field.value as string[]) ?? []}
                          onChange={(event) => field.onChange(event.target.value as string[])}
                          renderValue={(selected) => (selected as string[]).join(', ')}
                        >
                          {response.options?.map((option) => (
                            <MenuItem key={option} value={option}>
                              {option}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  />
                )
              case FormFieldType.Number:
                return <TextInputController key={response.fieldId} control={rhForm.control} fullWidth label={response.label} name={fieldName} type="number" />
              case FormFieldType.TextArea:
                return (
                  <TextInputController
                    key={response.fieldId}
                    control={rhForm.control}
                    fullWidth
                    label={response.label}
                    name={fieldName}
                    multiline
                    minRows={3}
                  />
                )
              case FormFieldType.Text:
                return <TextInputController key={response.fieldId} control={rhForm.control} fullWidth label={response.label} name={fieldName} />
              default:
                return null
            }
          })}
        </Stack>
      </AccordionDetails>
    </Accordion>
  )
}

export default GenerateReportDialog
